import { LIFECYCLE_CATEGORIES, canonicalNavigationRoute, type NavigationLayoutDocument, type NavigationView } from '@cdf/navigation-catalogue';

import { getCustomPageIcon } from '@/lib/custom-page-icons';
import {
  getStandardNavigationCatalogue,
  type NavItem,
  type NavSection
} from '@/lib/navigation';

export type NavigationPlacement = 'top' | 'resources' | 'admin';

export type ResolvedNavSection = NavSection & {
  id: string;
  placement: NavigationPlacement;
};

export interface NavigationCustomPage {
  id: string;
  slug: string;
  title: string;
  icon?: string | null;
}

export interface ResolveNavigationInput {
  view: NavigationView;
  standardSections: readonly NavSection[];
  adminSections: readonly NavSection[];
  customPages: readonly NavigationCustomPage[];
  layout: NavigationLayoutDocument | null;
  hasPermission: (permission: string) => boolean;
  enabledModules?: readonly string[];
}

type StandardEntry = {
  item: NavItem;
  placement: NavigationPlacement;
  sectionModuleKey?: string;
};

type ResolvedItem = {
  item: NavItem;
  placement?: NavigationPlacement;
};

const ADMIN_ROOT_ROUTES = new Set(['/admin/entity', '/admin/tenant', '/admin/platform']);
const standardCatalogue = getStandardNavigationCatalogue();
const standardViews = new Map(standardCatalogue.map((item) => [item.key, new Set(item.views)]));
const standardRoutes = new Set(standardCatalogue.map((item) => item.to));

function sectionPlacement(section: NavSection, admin: boolean): NavigationPlacement {
  if (admin) return 'admin';
  return section.heading === 'Resources' ? 'resources' : 'top';
}

function itemIsVisible(
  item: NavItem,
  sectionModuleKey: string | undefined,
  hasPermission: ResolveNavigationInput['hasPermission'],
  enabledModules: ReadonlySet<string> | null
): boolean {
  if (item.permissions && !item.permissions.some(hasPermission)) return false;
  const moduleKey = item.moduleKey ?? sectionModuleKey;
  return !moduleKey || !enabledModules || enabledModules.has(moduleKey);
}

function defaultSection(
  section: NavSection,
  placement: NavigationPlacement,
  hasPermission: ResolveNavigationInput['hasPermission'],
  enabledModules: ReadonlySet<string> | null,
  rootsOnly = false
): ResolvedNavSection | null {
  if (section.moduleKey && enabledModules && !enabledModules.has(section.moduleKey)) return null;
  const items = section.items.filter((item) => (
    (!rootsOnly || Boolean(item.to && ADMIN_ROOT_ROUTES.has(item.to))) &&
    itemIsVisible(item, section.moduleKey, hasPermission, enabledModules)
  ));
  return items.length || section.lifecycleCategory ? {
    ...section,
    id: `standard:${placement}:${section.heading}`,
    items: [...items],
    placement
  } : null;
}

function customPageNavItem(page: NavigationCustomPage, title?: string, icon?: string): NavItem {
  return {
    title: title ?? page.title,
    icon: getCustomPageIcon(icon ?? page.icon),
    to: `/pages/${page.slug}`
  };
}

function buildStandardLookup(
  standardSections: readonly NavSection[],
  adminSections: readonly NavSection[]
): Map<string, StandardEntry> {
  const entries = new Map<string, StandardEntry>();
  const addSections = (sections: readonly NavSection[], admin: boolean) => {
    for (const section of sections) {
      const placement = sectionPlacement(section, admin);
      for (const item of section.items) {
        if (item.key && !entries.has(item.key)) {
          entries.set(item.key, { item, placement, sectionModuleKey: section.moduleKey });
        }
        for (const child of item.children ?? []) {
          if (!entries.has(child.key)) {
            entries.set(child.key, {
              item: {
                key: child.key,
                title: child.title,
                icon: item.icon,
                to: child.to,
                permissions: item.permissions,
                moduleKey: item.moduleKey
              },
              placement,
              sectionModuleKey: section.moduleKey
            });
          }
        }
      }
    }
  };
  addSections(standardSections, false);
  addSections(adminSections, true);
  return entries;
}

function sectionsWithNewCustomPages(
  layout: NavigationLayoutDocument,
  customPages: readonly NavigationCustomPage[]
): NavigationLayoutDocument['sections'] {
  const sections = layout.sections.map((section) => ({
    ...section,
    items: [...section.items]
  }));
  const referenced = new Set(
    sections.flatMap((section) => section.items.flatMap((item) => item.kind === 'custom-page' ? [item.pageId] : []))
  );
  const unreferenced = customPages.filter((page) => !referenced.has(page.id));
  if (!unreferenced.length) return sections;

  let targetIndex = sections.findIndex((section) => section.items.some((item) => item.kind === 'custom-page'));
  if (targetIndex < 0) {
    targetIndex = sections.findIndex((section) => (
      section.id === 'custom-pages' || section.heading.trim().toLowerCase() === 'custom pages'
    ));
  }
  if (targetIndex < 0) {
    targetIndex = sections.findIndex(section => section.id === 'lifecycle-govern-improve');
    if (targetIndex < 0) {
      sections.push({ id: 'custom-pages', heading: 'Custom pages', items: [] });
      targetIndex = sections.length - 1;
    }
  }
  sections[targetIndex]!.items.push(...unreferenced.map((page) => ({
    kind: 'custom-page' as const,
    pageId: page.id,
    hidden: false
  })));
  return sections;
}

/**
 * Resolve the entity's sidebar document against current routes, pages and access.
 * Breadcrumbs and other navigation consumers intentionally keep using the
 * standard definitions because entity customisation applies only to the shell.
 */
export function resolveNavigation({
  view,
  standardSections,
  adminSections,
  customPages,
  layout,
  hasPermission,
  enabledModules
}: ResolveNavigationInput): ResolvedNavSection[] {
  const enabled = enabledModules ? new Set(enabledModules) : null;

  if (!layout) {
    const main = standardSections.flatMap((section) => {
      const resolved = defaultSection(
        section,
        sectionPlacement(section, false),
        hasPermission,
        enabled
      );
      return resolved ? [resolved] : [];
    });
    const top = main.filter((section) => section.placement === 'top');
    const resources = main.filter((section) => section.placement === 'resources');
    const custom = customPages.length ? [{
      id: 'custom-pages',
      heading: 'Custom pages',
      items: customPages.map((page) => customPageNavItem(page)),
      placement: 'top' as const
    }] : [];
    const admin = adminSections.flatMap((section) => {
      const resolved = defaultSection(section, 'admin', hasPermission, enabled, true);
      return resolved ? [resolved] : [];
    });
    const governance = top.find(section => section.lifecycleCategory === 'govern-improve');
    if (governance) {
      // Keep the three administration scopes named and permission-filtered.
      governance.items.push(...admin.flatMap(section => section.items));
      return [...top, ...custom, ...resources];
    }
    return [...top, ...custom, ...resources, ...admin];
  }

  const lookup = buildStandardLookup(standardSections, adminSections);
  const pages = new Map(customPages.map((page) => [page.id, page]));
  const sections = sectionsWithNewCustomPages(layout, customPages);
  const resolved: ResolvedNavSection[] = [];

  for (const section of sections) {
    const items: ResolvedItem[] = [];
    for (const configured of section.items) {
      if ((configured.kind === 'standard' || configured.kind === 'custom-page') && configured.hidden) continue;
      if (configured.kind === 'standard') {
        if (!standardViews.get(configured.key)?.has(view)) continue;
        const standard = lookup.get(configured.key);
        if (!standard) continue;
        const item = {
          ...standard.item,
          title: configured.title ?? standard.item.title,
          icon: configured.icon ? getCustomPageIcon(configured.icon) : standard.item.icon,
          moduleKey: standard.item.moduleKey ?? standard.sectionModuleKey
        };
        if (itemIsVisible(item, undefined, hasPermission, enabled)) {
          items.push({ item, placement: standard.placement });
        }
      } else if (configured.kind === 'custom-page') {
        const page = pages.get(configured.pageId);
        if (page) items.push({ item: customPageNavItem(page, configured.title, configured.icon) });
      } else if (configured.kind === 'external') {
        if (configured.href.startsWith('https://')) {
          items.push({ item: {
            title: configured.title,
            icon: getCustomPageIcon(configured.icon),
            href: configured.href,
            external: true
          } });
        }
      } else if (standardRoutes.has(canonicalNavigationRoute(configured.to))) {
        const canonicalRoute = canonicalNavigationRoute(configured.to);
        const candidates = standardCatalogue.filter(entry =>
          canonicalNavigationRoute(entry.to) === canonicalRoute);
        const permitted = candidates.find(entry =>
          (!canonicalRoute.startsWith('/admin/') || lookup.has(entry.key)) &&
          (!entry.moduleKey || !enabled || enabled.has(entry.moduleKey)) &&
          (!entry.permissions || entry.permissions.some(hasPermission)));
        if (!permitted) continue;
        items.push({ item: {
          title: configured.title,
          icon: getCustomPageIcon(configured.icon),
          to: canonicalRoute,
          moduleKey: permitted.moduleKey,
          permissions: permitted.permissions ? [...permitted.permissions] : undefined
        } });
      }
    }

    const lifecycleCategory = LIFECYCLE_CATEGORIES.find(category => section.id === 'lifecycle-' + category.id)?.id;
    if (lifecycleCategory || section.id === 'lifecycle-utilities') {
      resolved.push({ id: section.id, heading: lifecycleCategory === 'find' && section.heading === 'Find' ? 'Connect' : section.heading, items: items.map(entry => entry.item), placement: 'top', lifecycleCategory });
      continue;
    }
    const explicitPlacements = new Set(items.flatMap((entry) => entry.placement ? [entry.placement] : []));
    const inheritedPlacement = explicitPlacements.size === 1
      ? explicitPlacements.values().next().value ?? 'top'
      : 'top';
    const grouped = new Map<NavigationPlacement, NavItem[]>();
    for (const entry of items) {
      const placement = entry.placement ?? inheritedPlacement;
      const group = grouped.get(placement) ?? [];
      group.push(entry.item);
      grouped.set(placement, group);
    }
    for (const [placement, group] of grouped) {
      if (group.length) resolved.push({
        id: `${section.id}:${placement}`,
        heading: section.heading,
        items: group,
        placement
      });
    }
  }

  // Saved omissions and placement are deliberate, including the Agent entry.
  return resolved.filter(section => section.items.length > 0 || section.lifecycleCategory);

}
